import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// ─── JSON-LD ──────────────────────────────────────────────────

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'O tarot pode prever o futuro?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O tarot não é uma bola de cristal, ele revela padrões, energias e possibilidades presentes no momento da consulta. As cartas refletem o estado interno e as tendências da situação, ajudando a tomar decisões mais conscientes. O futuro é aberto e moldado pelas escolhas de cada um.',
      },
    },
    {
      '@type': 'Question',
      name: 'Qual a diferença entre tarot e outros oráculos?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'O tarot tem um sistema estruturado de 78 cartas divididas em Arcanos Maiores e Menores, com simbologia universal baseada em arquétipos psicológicos. Outros oráculos, como Lenormand ou cartas de afirmação, têm sistemas próprios e menos camadas de interpretação. O tarot é considerado o mais completo e versátil.',
      },
    },
    {
      '@type': 'Question',
      name: 'Preciso acreditar no sobrenatural para usar o tarot?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Não. Muitas pessoas usam o tarot como ferramenta de reflexão psicológica, similar ao que Carl Jung chamava de sincronicidade. As cartas funcionam como espelhos simbólicos que ajudam a acessar intuições e perspectivas que já estão dentro de você.',
      },
    },
    {
      '@type': 'Question',
      name: 'Como o tarot personalizado com mapa natal é diferente?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Na Professya, cada leitura de tarot considera as posições planetárias do seu mapa natal e os trânsitos atuais. Isso significa que a interpretação das cartas é contextualizada para o seu momento astrológico específico, tornando as respostas muito mais precisas e relevantes do que uma leitura genérica.',
      },
    },
  ],
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://professya.com' },
    { '@type': 'ListItem', position: 2, name: 'Tarot', item: 'https://professya.com/taro' },
  ],
};

// ─── Component ───────────────────────────────────────────────

export function TaroPage() {
  return (
    <>
      <Helmet>
        <title>Tarot Online Grátis: O que é e Como Funciona, Professya</title>
        <meta name="description" content="Aprenda sobre tarot, seus arcanos e tiragens. Faça uma leitura de tarot personalizada baseada no seu mapa natal para respostas mais precisas." />
        <meta property="og:title" content="Tarot Online Grátis: O que é e Como Funciona, Professya" />
        <meta property="og:description" content="Aprenda sobre tarot, seus arcanos e tiragens. Faça uma leitura de tarot personalizada baseada no seu mapa natal para respostas mais precisas." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://professya.com/taro" />
        <link rel="canonical" href="https://professya.com/taro" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(180deg, rgba(245,200,66,0.08) 0%, transparent 100%)', padding: '3rem 1.5rem 2rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textDecoration: 'none', display: 'inline-block', marginBottom: '1.5rem' }}>
            ✦ Professya
          </Link>
          <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>🃏</div>
          <h1 className="font-display" style={{ fontSize: 'clamp(2rem,6vw,3rem)', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
            Tarot: O Oráculo das 78 Cartas
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '640px', margin: '0 auto', lineHeight: 1.7, fontSize: 'var(--text-body)' }}>
            Tudo sobre tarot, origem, arcanos, tipos de tiragem e como uma leitura personalizada com seu mapa natal pode oferecer respostas mais profundas.
          </p>
        </div>

        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>

          {/* Section 1, O que é tarot */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem', color: 'var(--petrol)' }}>
              O que é tarot
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>
              O tarot é um sistema de 78 cartas com imagens simbólicas que remonta ao século XV, originalmente usado como jogo de cartas na Europa e posteriormente adotado como ferramenta de adivinhação e autoconhecimento. Cada carta carrega arquétipos universais, figuras, situações e energias que ressoam com experiências humanas fundamentais. O tarotista, ou a IA, no caso de ferramentas digitais, interpreta a combinação das cartas tiradas em uma tiragem, criando uma narrativa sobre o momento presente, os padrões em jogo e as possíveis direções de uma situação. Hoje, o tarot é amplamente usado como instrumento de reflexão psicológica, aconselhamento espiritual e suporte na tomada de decisões.
            </p>
          </section>

          {/* Section 2, Arcanos */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '1rem' }}>
              Arcanos Maiores e Menores
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div className="card" style={{ borderColor: 'var(--border-gold)' }}>
                <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600, marginBottom: '0.6rem', color: 'var(--petrol)' }}>
                  22 Arcanos Maiores
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', lineHeight: 1.75 }}>
                  Representam temas universais e grandes forças da vida: O Louco, A Sacerdotisa, A Torre, O Mundo. São as cartas de maior peso simbólico em uma tiragem, indicando transformações profundas, lições de alma e eventos de grande impacto. Quando aparecem em maioria numa tiragem, o tema em questão tem relevância kármica ou de longo prazo.
                </p>
              </div>
              <div className="card">
                <h3 style={{ fontSize: 'var(--text-body)', fontWeight: 600, marginBottom: '0.6rem' }}>
                  56 Arcanos Menores
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', lineHeight: 1.75 }}>
                  Divididos em quatro naipes, Paus (fogo/ação), Copas (água/emoção), Espadas (ar/mente) e Ouros (terra/matéria), os Arcanos Menores tratam das situações cotidianas: relacionamentos, trabalho, desafios práticos e oportunidades do dia a dia. Cada naipe vai do Ás ao 10, mais quatro cartas da corte: Pajem, Cavaleiro, Rainha e Rei.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3, Tiragens */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '1rem' }}>
              Tipos de tiragens
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              {[
                {
                  cards: '1 carta',
                  icon: '🃏',
                  title: 'Carta do dia',
                  desc: 'A tiragem mais simples. Uma única carta revela a energia dominante do dia ou uma resposta direta a uma pergunta objetiva. Ideal para reflexão diária e desenvolvimento da intuição.',
                },
                {
                  cards: '3 cartas',
                  icon: '🃏🃏🃏',
                  title: 'Passado, Presente, Futuro',
                  desc: 'A tiragem mais versátil e popular. Três cartas revelam a origem de uma situação, onde ela está agora e para onde tende a se encaminhar. Pode ser adaptada para Mente–Corpo–Espírito ou Situação–Ação–Resultado.',
                },
                {
                  cards: '10 cartas',
                  icon: '✦',
                  title: 'Cruz Celta',
                  desc: 'A tiragem mais completa e detalhada do tarot. Dez cartas exploram o tema central, os obstáculos, a base inconsciente, o passado, o futuro possível, a posição do consulente, influências externas, esperanças, medos e o desfecho provável. Ideal para questões complexas de vida.',
                },
              ].map(item => (
                <div key={item.title} className="card" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '1.25rem', flexShrink: 0, paddingTop: '0.1rem' }}>{item.icon}</div>
                  <div>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'baseline', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: 'var(--text-label)', fontWeight: 600, margin: 0 }}>{item.title}</h3>
                      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 500 }}>{item.cards}</span>
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 4, Personalizado vs genérico */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem' }}>
              Tarot personalizado vs genérico
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85, marginBottom: '1rem' }}>
              A maioria dos aplicativos de tarot online sorteia cartas aleatoriamente e oferece interpretações padrão, o mesmo texto para qualquer pessoa que tirar a mesma carta. O tarot personalizado da Professya funciona de forma diferente: ele integra as posições dos planetas no seu mapa natal com os trânsitos astrológicos atuais, contextualizando cada carta dentro do seu momento cósmico específico.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
              <div className="card" style={{ opacity: 0.7 }}>
                <h3 style={{ fontSize: 'var(--text-label)', fontWeight: 600, marginBottom: '0.6rem', color: 'var(--text-muted)' }}>
                  Tarot genérico
                </h3>
                <ul style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
                  <li>Interpretações fixas para cada carta</li>
                  <li>Sem consideração do momento pessoal</li>
                  <li>Igual para todo mundo</li>
                  <li>Sem contexto astrológico</li>
                </ul>
              </div>
              <div className="card" style={{ borderColor: 'var(--border-gold)' }}>
                <h3 style={{ fontSize: 'var(--text-label)', fontWeight: 600, marginBottom: '0.6rem', color: 'var(--petrol)' }}>
                  Tarot Professya ✦
                </h3>
                <ul style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
                  <li>Interpretação baseada no seu mapa natal</li>
                  <li>Considera os trânsitos do momento</li>
                  <li>Contextualizado para sua vida real</li>
                  <li>IA treinada em astrologia e tarot</li>
                </ul>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '1.25rem' }}>
              Perguntas frequentes sobre tarot
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {faqSchema.mainEntity.map((item, i) => (
                <details key={i} className="card" style={{ cursor: 'pointer' }}>
                  <summary style={{ fontWeight: 600, fontSize: 'var(--text-label)', color: 'var(--text-primary)', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                    {item.name}
                    <span style={{ color: 'var(--petrol)', flexShrink: 0 }}>+</span>
                  </summary>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginTop: '0.75rem', fontSize: 'var(--text-label)' }}>
                    {item.acceptedAnswer.text}
                  </p>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="card" style={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(245,200,66,0.06) 0%, transparent 100%)', border: '1px solid rgba(245,200,66,0.2)' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🃏</div>
            <p className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              Pronto para sua primeira tiragem?
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>
              Faça uma leitura de tarot personalizada com base no seu mapa natal. Crie sua conta gratuitamente.
            </p>
            <Link to="/register" className="btn btn-primary">
              Fazer minha primeira tiragem →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
